/**
 * Letters of Credit tracker.
 *
 * Mock-aware: when `useMockData()` is true, reads from
 * local mock data. Real-mode wiring is left as a TODO until
 * InstantDB queries are connected.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Filter, Pencil, Plus, Ship } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import {
  Badge,
  Button,
  DataTable,
  KpiCard,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  LcStatusBadge,
  type LcStatus,
} from "../../../components/imports/LcStatusBadge";
import { PoStatusBadge } from "../../../components/imports/PoStatusBadge";
import {
  findSupplier,
  mockLettersOfCredit,
  mockPurchaseOrders,
  mockSuppliers,
  mockFxRates,
  poCountForLc,
} from "../../../components/imports/importsMockData";

type MockSupplier = (typeof mockSuppliers)[number];
type MockPurchaseOrder = (typeof mockPurchaseOrders)[number];
type MockFxRateRow = (typeof mockFxRates)[number];
type LcRaw = (typeof mockLettersOfCredit)[number];
import { LcForm, type LcFormValues } from "../../../components/forms/LcForm";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { useHasPermission } from "../../../lib/permissions";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";
import {
  AttachmentBadge,
  AttachmentList,
} from "../../../components/attachments";

/** LC document categories — see SWIFT MT700 / customs paperwork conventions. */
const LC_PURPOSE_OPTIONS = [
  { value: "lc_application", label: "LC Application" },
  { value: "bl", label: "Bill of Lading" },
  { value: "commercial_invoice", label: "Commercial Invoice" },
  { value: "packing_list", label: "Packing List" },
  { value: "coo", label: "Certificate of Origin" },
  { value: "insurance", label: "Insurance" },
  { value: "inspection", label: "Inspection" },
];

export const Route = createFileRoute("/_app/imports/lc")({
  component: LcTrackerPage,
});

const NOW = Date.now();
const DAY = 24 * 60 * 60 * 1000;

type LcRow = {
  _id: string;
  lcNumber: string;
  bankName: string;
  supplierId: string;
  status: LcStatus;
  issueDate: number;
  expiryDate: number;
  currency: string;
  amountCents: bigint;
  amountInBaseCents: bigint;
  drawnAmountCents: bigint;
  notes: string | null;
};

function toLcRow(raw: LcRaw): LcRow {
  return {
    _id: raw._id,
    lcNumber: raw.lcNumber,
    bankName: raw.bankName,
    supplierId: raw.supplierId,
    status: raw.status,
    issueDate: raw.issueDate,
    expiryDate: raw.expiryDate,
    currency: raw.currency,
    amountCents: raw.amountCents,
    amountInBaseCents: raw.amountInBaseCents,
    drawnAmountCents: raw.drawnAmountCents,
    notes: raw.notes,
  };
}

const STATUS_OPTIONS: { value: LcStatus | "all"; label: string }[] = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "partially_used", label: "Partially used" },
  { value: "closed", label: "Closed" },
  { value: "cancelled", label: "Cancelled" },
];

function fxRateForCurrency(
  code: string,
  fxRates: MockFxRateRow[],
): bigint {
  const row = fxRates.find(
    (r) => r.fromCurrency === code.toUpperCase() && r.toCurrency === "BDT",
  );
  return row?.rateE8 ?? 100_000_000n;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-BD", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

function LcTrackerPage() {
  const isMock = useMockData();
  // LC amounts are commercial-sensitive — gate columns + KPI behind finance.
  const canSeeAmounts = useHasPermission("finance.read");

  const lcSeed = useMemo(
    () => (mockLettersOfCredit as readonly LcRaw[]).map(toLcRow),
    [],
  );
  const [seedRows, setSeedRows] = usePersistentMockState<LcRow[]>("lettersOfCredit", lcSeed);
  const rows = seedRows;

  const [suppliers] = usePersistentMockState<MockSupplier[]>(
    "suppliers",
    mockSuppliers,
  );
  const [purchaseOrders] = usePersistentMockState<MockPurchaseOrder[]>(
    "purchaseOrders",
    mockPurchaseOrders,
  );
  const [fxRates] = usePersistentMockState<MockFxRateRow[]>(
    "fxRates",
    mockFxRates,
  );

  const [filterStatus, setFilterStatus] = useState<LcStatus | "all">("all");
  const [filterSupplier, setFilterSupplier] = useState<string | "all">("all");
  const [filterYear, setFilterYear] = useState<number | "all">("all");

  const [createOpen, setCreateOpen] = useState(false);
  const [detailRow, setDetailRow] = useState<LcRow | null>(null);
  const [editingLc, setEditingLc] = useState<LcRow | null>(null);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterSupplier !== "all" && r.supplierId !== filterSupplier)
        return false;
      if (filterYear !== "all") {
        const yr = new Date(r.issueDate).getFullYear();
        if (yr !== filterYear) return false;
      }
      return true;
    });
  }, [rows, filterStatus, filterSupplier, filterYear]);

  // KPIs
  const openLcs = useMemo(
    () =>
      rows.filter(
        (r) => r.status === "issued" || r.status === "partially_used",
      ),
    [rows],
  );
  const totalOpenBdtCents = useMemo(
    () => openLcs.reduce((s, r) => s + r.amountInBaseCents, 0n),
    [openLcs],
  );
  const expiringSoon = useMemo(
    () =>
      rows.filter(
        (r) =>
          (r.status === "issued" || r.status === "partially_used") &&
          r.expiryDate - NOW <= 30 * DAY &&
          r.expiryDate >= NOW,
      ).length,
    [rows],
  );

  const yearOptions = useMemo(() => {
    const ys = new Set<number>();
    rows.forEach((r) => ys.add(new Date(r.issueDate).getFullYear()));
    return Array.from(ys).sort((a, b) => b - a);
  }, [rows]);

  const columns = useMemo<ColumnDef<LcRow>[]>(
    () => {
      const cols: ColumnDef<LcRow>[] = [
        {
          accessorKey: "lcNumber",
          header: "LC Number",
          cell: ({ row }) => (
            <span className="font-mono text-[12px] text-(--text-primary)">
              {row.original.lcNumber}
            </span>
          ),
          meta: { mono: true },
        },
        {
          accessorKey: "bankName",
          header: "Bank",
          cell: ({ row }) => (
            <span className="text-(--text-primary)">
              {row.original.bankName}
            </span>
          ),
        },
        {
          id: "beneficiary",
          header: "Beneficiary",
          cell: ({ row }) => {
            const sup = findSupplier(row.original.supplierId);
            return (
              <span className="text-(--text-secondary)">
                {sup?.displayName ?? "—"}
              </span>
            );
          },
        },
      ];

      if (canSeeAmounts) {
        cols.push(
          {
            id: "amount",
            header: "Amount",
            cell: ({ row }) => (
              <span className="font-mono tabular-nums text-(--text-primary)">
                {formatMoney(row.original.amountCents, row.original.currency, {
                  display: "code",
                  locale: "en-BD",
                })}
              </span>
            ),
            meta: { numeric: true, mono: true },
          },
          {
            id: "amountBdt",
            header: "Amount (BDT)",
            cell: ({ row }) => (
              <span className="font-mono tabular-nums text-(--text-secondary)">
                {formatMoney(row.original.amountInBaseCents, "BDT", {
                  locale: "en-BD",
                })}
              </span>
            ),
            meta: { numeric: true, mono: true, hideOnMobile: true },
          },
        );
      }

      cols.push(
        {
          accessorKey: "issueDate",
          header: "Issue date",
          cell: ({ row }) => (
            <span className="text-(--text-secondary) tabular-nums">
              {formatDate(row.original.issueDate)}
            </span>
          ),
          meta: { hideOnMobile: true },
        },
        {
          accessorKey: "expiryDate",
          header: "Expiry",
          cell: ({ row }) => {
            const days = Math.round((row.original.expiryDate - NOW) / DAY);
            const tone =
              days < 0
                ? "text-(--danger)"
                : days < 30
                  ? "text-(--warning)"
                  : "text-(--text-secondary)";
            return (
              <span className={`tabular-nums ${tone}`}>
                {formatDate(row.original.expiryDate)}
              </span>
            );
          },
        },
        {
          accessorKey: "status",
          header: "Status",
          cell: ({ row }) => <LcStatusBadge status={row.original.status} />,
        },
        {
          id: "linkedPos",
          header: "POs",
          cell: ({ row }) => (
            <Badge variant="neutral">{poCountForLc(row.original._id)}</Badge>
          ),
        },
      );

      return cols;
    },
    [canSeeAmounts],
  );

  function handleCreate(values: LcFormValues) {
    const id = `lc_local_${Date.now()}`;
    const fxRate = fxRateForCurrency(values.currency, fxRates);
    const issueDate = new Date(values.issueDate).getTime();
    const expiryDate = new Date(values.expiryDate).getTime();
    const amountInBaseCents =
      (values.lcAmountCents * fxRate) / 100_000_000n;
    const newRow: LcRow = {
      _id: id,
      lcNumber: `LC-${new Date(issueDate).getFullYear()}-${String(rows.length + 1).padStart(6, "0")}`,
      bankName: values.bankName,
      supplierId: values.supplierId,
      status: "draft",
      issueDate,
      expiryDate,
      currency: values.currency,
      amountCents: values.lcAmountCents,
      amountInBaseCents,
      drawnAmountCents: 0n,
      notes: values.notes || null,
    };
    setSeedRows((prev) => [newRow, ...prev]);
    setCreateOpen(false);
    toast.success(`LC ${newRow.lcNumber} created (mock)`);
  }

  function handleEditSubmit(values: LcFormValues) {
    if (!editingLc) return;
    const fxRate = fxRateForCurrency(values.currency, fxRates);
    const issueDate = new Date(values.issueDate).getTime();
    const expiryDate = new Date(values.expiryDate).getTime();
    const amountInBaseCents =
      (values.lcAmountCents * fxRate) / 100_000_000n;
    setSeedRows((prev) =>
      prev.map((r) =>
        r._id === editingLc._id
          ? {
              ...r,
              bankName: values.bankName,
              supplierId: values.supplierId,
              issueDate,
              expiryDate,
              currency: values.currency,
              amountCents: values.lcAmountCents,
              amountInBaseCents,
              notes: values.notes || null,
            }
          : r,
      ),
    );
    setDetailRow((prev) =>
      prev && prev._id === editingLc._id
        ? {
            ...prev,
            bankName: values.bankName,
            supplierId: values.supplierId,
            issueDate,
            expiryDate,
            currency: values.currency,
            amountCents: values.lcAmountCents,
            amountInBaseCents,
            notes: values.notes || null,
          }
        : prev,
    );
    setEditingLc(null);
    toast.success(`Updated ${editingLc.lcNumber}. Audit trail recorded.`);
  }

  function handleExportCsv(extension: "csv" | "xls") {
    const columns: ExportColumn<LcRow>[] = [
      { key: "lcNumber", label: "LC #" },
      { key: "bankName", label: "Bank" },
      {
        key: "supplierId",
        label: "Supplier",
        value: (r) => findSupplier(r.supplierId)?.displayName ?? r.supplierId,
      },
      { key: "status", label: "Status" },
      { key: "issueDate", label: "Issue date", format: (v) => formatDateForCsv(Number(v)) },
      { key: "expiryDate", label: "Expiry", format: (v) => formatDateForCsv(Number(v)) },
      { key: "amountCents", label: "Amount" },
      { key: "currency", label: "Currency" },
      { key: "amountInBaseCents", label: "Amount BDT" },
      { key: "drawnAmountCents", label: "Drawn amount" },
    ];
    downloadCsv(exportFilename("letters-of-credit", extension), toCsv(filtered, columns));
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Letters of Credit"
        description="Track LC issuance, drawdowns, and bank fees against incoming shipments."
        actions={
          <>
            {canSeeAmounts ? (
              <ExportButton
                onCsv={() => handleExportCsv("csv")}
                onExcel={() => handleExportCsv("xls")}
              />
            ) : null}
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New LC
            </Button>
          </>
        }
      />

      {/* KPIs */}
      <section
        className={
          canSeeAmounts
            ? "grid grid-cols-1 sm:grid-cols-3 gap-4"
            : "grid grid-cols-1 sm:grid-cols-2 gap-4"
        }
        aria-label="LC summary"
      >
        <KpiCard
          caption="Open LCs"
          value={openLcs.length}
          hint="Issued or partially used"
        />
        {canSeeAmounts ? (
          <KpiCard
            caption="Total open value"
            value={formatMoneySmart(totalOpenBdtCents, "BDT", { locale: "en-BD" })}
            hint="In BDT (snapshot rates)"
          />
        ) : null}
        <KpiCard
          caption="Expiring within 30d"
          value={expiringSoon}
          hint="Action required if drawdown is incomplete"
        />
      </section>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Filter
          className="h-3.5 w-3.5 text-(--text-tertiary)"
          aria-hidden
        />
        <FilterChip
          label="Status"
          value={filterStatus}
          options={STATUS_OPTIONS.map((s) => ({
            value: s.value,
            label: s.label,
          }))}
          onChange={(v) => setFilterStatus(v as LcStatus | "all")}
        />
        <FilterChip
          label="Supplier"
          value={filterSupplier}
          options={[
            { value: "all", label: "All suppliers" },
            ...suppliers.map((s) => ({
              value: s._id,
              label: s.displayName,
            })),
          ]}
          onChange={setFilterSupplier}
        />
        <FilterChip
          label="Year"
          value={filterYear === "all" ? "all" : String(filterYear)}
          options={[
            { value: "all", label: "All years" },
            ...yearOptions.map((y) => ({ value: String(y), label: String(y) })),
          ]}
          onChange={(v) => setFilterYear(v === "all" ? "all" : Number(v))}
        />
      </div>

      <DataTable
        data={filtered}
        columns={columns}
        getRowId={(r) => r._id}
        onRowClick={(row) => setDetailRow(row.original)}
        emptyTitle="No LCs match"
        emptyDescription={
          rows.length === 0
            ? "Issue an LC to start tracking foreign drawdowns."
            : "Try widening your filters."
        }
        emptyIcon={<Ship className="h-5 w-5" aria-hidden />}
        emptyAction={
          rows.length === 0 ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Open new LC
            </Button>
          ) : undefined
        }
      />

      {/* Create LC */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New Letter of Credit</SheetTitle>
            <SheetDescription>
              FX rate is snapshotted at posting time and frozen for the LC.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <CreateLcContainer
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
              suppliers={suppliers}
              fxRates={fxRates}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet
        open={detailRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailRow(null);
            setEditingLc(null);
          }
        }}
      >
        <SheetContent side="right" size="lg">
          {detailRow && editingLc ? (
            <>
              <SheetHeader>
                <SheetTitle>Edit {editingLc.lcNumber}</SheetTitle>
                <SheetDescription>
                  Changes are appended to the audit trail. Closed LCs are
                  read-only.
                </SheetDescription>
              </SheetHeader>
              <SheetBody>
                <EditLcContainer
                  row={editingLc}
                  onSubmit={handleEditSubmit}
                  onCancel={() => setEditingLc(null)}
                  suppliers={suppliers}
                  fxRates={fxRates}
                />
              </SheetBody>
            </>
          ) : detailRow ? (
            <LcDetail
              row={detailRow}
              onEdit={() => setEditingLc(detailRow)}
              purchaseOrders={purchaseOrders}
            />
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Edit container — provides FX context for the edit form.
 * ───────────────────────────────────────────────────────────── */

function EditLcContainer({
  row,
  onSubmit,
  onCancel,
  suppliers,
  fxRates,
}: {
  row: LcRow;
  onSubmit: (v: LcFormValues) => void;
  onCancel: () => void;
  suppliers: MockSupplier[];
  fxRates: MockFxRateRow[];
}) {
  const [currency, setCurrency] = useState(row.currency);
  const fxRateE8 = fxRateForCurrency(currency, fxRates);
  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((s) => s.deletedAt === null)
        .map((s) => ({
          id: s._id,
          displayName: s.displayName,
          defaultCurrency: s.defaultCurrency,
        })),
    [suppliers],
  );
  return (
    <LcForm
      submitLabel="Save changes"
      suppliers={supplierOptions}
      fxRateE8={fxRateE8}
      baseCurrency="BDT"
      initialValues={{
        supplierId: row.supplierId,
        bankName: row.bankName,
        bankReference: "",
        lcAmountCents: row.amountCents,
        currency: row.currency,
        issueDate: new Date(row.issueDate).toISOString().slice(0, 10),
        expiryDate: new Date(row.expiryDate).toISOString().slice(0, 10),
        incoterms: "FOB",
        notes: row.notes ?? "",
      }}
      onSubmit={(values) => {
        setCurrency(values.currency);
        return onSubmit(values);
      }}
      onCancel={onCancel}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Create container — owns FX lookup state for the form.
 * ───────────────────────────────────────────────────────────── */

function CreateLcContainer({
  onSubmit,
  onCancel,
  suppliers,
  fxRates,
}: {
  onSubmit: (v: LcFormValues) => void;
  onCancel: () => void;
  suppliers: MockSupplier[];
  fxRates: MockFxRateRow[];
}) {
  const [currency, setCurrency] = useState("USD");
  const fxRateE8 = fxRateForCurrency(currency, fxRates);
  const supplierOptions = useMemo(
    () =>
      suppliers
        .filter((s) => s.deletedAt === null)
        .map((s) => ({
          id: s._id,
          displayName: s.displayName,
          defaultCurrency: s.defaultCurrency,
        })),
    [suppliers],
  );
  return (
    <LcForm
      suppliers={supplierOptions}
      fxRateE8={fxRateE8}
      baseCurrency="BDT"
      onSubmit={(values) => {
        setCurrency(values.currency);
        return onSubmit(values);
      }}
      onCancel={onCancel}
      initialValues={{ currency }}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Detail Sheet
 * ───────────────────────────────────────────────────────────── */

function LcDetail({
  row,
  onEdit,
  purchaseOrders,
}: {
  row: LcRow;
  onEdit: () => void;
  purchaseOrders: MockPurchaseOrder[];
}) {
  const supplier = findSupplier(row.supplierId);
  const linkedPOs = useMemo(
    () => purchaseOrders.filter((p) => p.letterOfCreditId === row._id),
    [purchaseOrders, row._id],
  );
  const isLocked = row.status === "closed" || row.status === "cancelled";
  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3 flex-wrap">
          <span className="font-mono">{row.lcNumber}</span>
          <LcStatusBadge status={row.status} />
          <RecordAuditBadge tableName="lettersOfCredit" recordId={row._id} />
          <AttachmentBadge
            parentTable="lettersOfCredit"
            parentRecordId={row._id}
          />
        </SheetTitle>
        <SheetDescription>
          {row.bankName} · {supplier?.displayName ?? "—"}
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          {/* Money summary */}
          <section className="grid grid-cols-2 gap-3">
            <DetailField
              label="Face value"
              value={formatMoney(row.amountCents, row.currency, {
                display: "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="In BDT"
              value={formatMoney(row.amountInBaseCents, "BDT", {
                locale: "en-BD",
              })}
            />
            <DetailField
              label="Drawn"
              value={formatMoney(row.drawnAmountCents, row.currency, {
                display: "code",
                locale: "en-BD",
              })}
            />
            <DetailField
              label="Available"
              value={formatMoney(
                row.amountCents - row.drawnAmountCents,
                row.currency,
                { display: "code", locale: "en-BD" },
              )}
            />
            <DetailField label="Issued" value={formatDate(row.issueDate)} />
            <DetailField label="Expiry" value={formatDate(row.expiryDate)} />
          </section>

          {row.notes ? (
            <section>
              <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-secondary) whitespace-pre-line">
                {row.notes}
              </p>
            </section>
          ) : null}

          {/* Linked POs */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Linked POs ({linkedPOs.length})
            </div>
            {linkedPOs.length === 0 ? (
              <div className="text-[12px] text-(--text-tertiary)">
                No POs are linked to this LC yet.
              </div>
            ) : (
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-(--text-tertiary) border-b border-(--border-subtle)">
                    <th className="py-2">PO #</th>
                    <th>Status</th>
                    <th className="text-right">Total ({row.currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {linkedPOs.map((p) => (
                    <tr
                      key={p._id}
                      className="border-b border-(--border-subtle)"
                    >
                      <td className="py-2 font-mono text-[12px]">
                        {p.poNumber}
                      </td>
                      <td>
                        <PoStatusBadge status={p.status} size="sm" />
                      </td>
                      <td className="text-right font-mono tabular-nums">
                        {formatMoney(p.totalAmountCents, p.currency, {
                          display: "code",
                          locale: "en-BD",
                        })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Status timeline */}
          <section>
            <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary) mb-2">
              Status timeline
            </div>
            <ul className="text-[13px] flex flex-col gap-1">
              <li className="flex justify-between text-(--text-secondary)">
                <span>Created</span>
                <span className="tabular-nums">
                  {formatDate(row.issueDate)}
                </span>
              </li>
              <li className="flex justify-between text-(--text-secondary)">
                <span>Current status</span>
                <span className="font-medium text-(--text-primary)">
                  <LcStatusBadge status={row.status} size="sm" />
                </span>
              </li>
            </ul>
          </section>
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="lettersOfCredit" recordId={row._id} />
          </TabsContent>
          <TabsContent value="documents" className="mt-4">
            <AttachmentList
              parentTable="lettersOfCredit"
              parentRecordId={row._id}
              purposeOptions={LC_PURPOSE_OPTIONS}
              allowCustomPurpose
              mimeAllowlist={["image/*", "application/pdf"]}
              title="Drop LC documents here"
              description="BL, commercial invoice, packing list, CoO, etc. Max 10 MB per file."
            />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {isLocked ? (
          <Tooltip
            content="Cannot edit closed records — create a credit note instead"
          >
            <span>
              <Button variant="ghost" size="icon" disabled aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
            <Pencil className="size-4" />
          </Button>
        )}
        {row.status === "draft" ? (
          <Button
            variant="default"
            onClick={() => toast.info("Issue LC — wired in next phase")}
          >
            Issue LC
          </Button>
        ) : null}
        {row.status === "issued" || row.status === "partially_used" ? (
          <Button
            variant="outline"
            onClick={() => toast.info("Close — wired in next phase")}
          >
            Close LC
          </Button>
        ) : null}
        {row.status !== "closed" && row.status !== "cancelled" ? (
          <Button
            variant="destructive"
            onClick={() => toast.info("Cancel — wired in next phase")}
          >
            Cancel
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

function DetailField({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </div>
      <div className="text-[13px] text-(--text-primary) font-mono tabular-nums">
        {value}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Filter chip — small dropdown wrapper. Mirrors the Catalog page.
 * ───────────────────────────────────────────────────────────── */

function FilterChip<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 py-1 text-[12px]">
      <span className="text-(--text-tertiary)">{label}:</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="bg-transparent text-(--text-primary) outline-none cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
